/**
 * @file AnnotationsTableContainer.js
 * @description lists annotations with options to call
 *              AnnotationView and annotator for selected
 *              annotations
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Feb 05, 2023 16:17
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AnnotationsTableForm from './AnnotationsTableForm';
import AnnotationsTableFormFooter from './AnnotationsTableFormFooter';

import AnnotationGroupDialog from 'src/content/DocumentAnnotator/AnnotationGroup/AnnotationGroupDialog';

import { arrayExists, stringExists } from 'src/@common/utilities';

import {
    annotationViewCreateRequest,
} from 'src/store/actions/annotationView.actions';

import {
    annotationFindRequest,
} from 'src/store/actions/documentAnnotator/documentAnnotatorDb.actions';

function AnnotationsTableContainer(props) {
    const {
        annotations = [],
        containerSx = {
            my: '10px',
            ml: '10px',
            mr: '4px',
        }, // sx style for container
        contentSx = { width: '100%', mr: '5px', mb: '5px' }, // sx style for content
        currentPageNo = 1,

        noAnnotationsMsg = 'No annotations',
        pageCount,

        deletable = false,   // must be selectable to be deletable
        editable = false,
        selectable = false,
        viewable = false,

        tableSx = { width: '100%', mr: '5px', mb: '5px' }, // sx style for table
        tableTitle = 'Annotations',

        handleDeleteRows,
        handleCheckAllChange,
        handleCheckChange,
        handlePageNoChange,
    } = props;

    const dispatch = useDispatch();

    // start: redux state --------------------------
    const globalState = useSelector((state) => {
        return state.global;
    });

    const { dataLoading } = globalState;
    // end: redux state -----------------------------

    const [rowsAreSelected, setRowsAreSelected] = useState(false);
    const [tableRows, setTableRows] = useState([]);

    useEffect(() => {
        setTableRows(annotations);
    }, [annotations]);

    useEffect(() => {
        let selected = false;

        if (arrayExists(annotations)) {
            annotations.forEach((row) => {
                if (row.selected === true) {
                    selected = true;
                }
            });
        }

        setRowsAreSelected(selected);

    }, [annotations]);

    const handleEdit = (annotationId) => {
        if (stringExists(annotationId)) {
            dispatch(annotationFindRequest(annotationId));
        }
    };

    const handleView = (annotationId) => {
        if (stringExists(annotationId)) {
            dispatch(annotationViewCreateRequest(annotationId));
        }
    };

    return (
        <>
            <Card sx={containerSx} variant="outlined">

                <CardActions style={{ marginBottom: '0px', marginTop: '5px', marginLeft: '10px' }}>

                    <Stack
                        direction="row"
                        justifyContent="space-evenly"
                        alignItems="flex-start"
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                justifyContent: "flex-start",
                                alignItems: "center",
                            }}
                        >
                            <Typography component="span" variant="subtitle1">
                                {'Found: '}
                            </Typography>
                            <b>{tableTitle}</b>
                        </Stack>

                        {rowsAreSelected && deletable && selectable &&

                            <Tooltip title={'Delete selected rows'}>

                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    sx={{
                                        ml: '25px',
                                        mt: '-4px'
                                    }}
                                    loading={dataLoading}
                                    onClick={(ev) => {
                                        ev.preventDefault();
                                        ev.stopPropagation();
                                        handleDeleteRows();
                                    }}
                                >
                                    Delete
                                </Button>

                            </Tooltip>
                        }

                    </Stack>

                </CardActions>

                <CardContent sx={contentSx}>

                    {!dataLoading && !arrayExists(tableRows) &&
                        <Typography
                            align={'left'}
                            className="text-14 font-medium mt-15 ml-20">
                            {noAnnotationsMsg}
                        </Typography>
                    }

                    <AnnotationsTableForm
                        rows={tableRows}
                        rowsAreSelected={rowsAreSelected}
                        deletable={deletable}
                        editable={editable}
                        selectable={selectable}
                        tableSx={tableSx}
                        pageCount={pageCount}
                        viewable={viewable}
                        handleCheckAllChange={handleCheckAllChange}
                        handleCheckChange={handleCheckChange}
                        handleEdit={handleEdit}
                        handleView={handleView}
                    />

                </CardContent>

                <CardActions style={{ marginBottom: '10px', marginLeft: '10px' }}>

                    <Stack
                        direction="row"
                        justifyContent="space-evenly"
                        alignItems="flex-start"
                        spacing={3}
                    >

                        {pageCount > 1 &&
                            < AnnotationsTableFormFooter
                                currentPageNo={currentPageNo}
                                pageCount={pageCount}
                                handleChange={handlePageNoChange}
                            />
                        }

                    </Stack>

                </CardActions>

            </Card >

            <AnnotationGroupDialog
                title={'Edit Annotation'}
                canAddAnnotation={false}
                canDeleteAnnotation={false}
            />

        </>
    )
}

AnnotationsTableContainer.propTypes = {
    annotations: PropTypes.array,
    containerSx: PropTypes.object,
    contentSx: PropTypes.object,
    currentPageNo: PropTypes.number.isRequired,
    deletable: PropTypes.bool,
    editable: PropTypes.bool,
    tableSx: PropTypes.object,
    pageCount: PropTypes.number,
    noAnnotationsMsg: PropTypes.string,
    selectable: PropTypes.bool,
    tableTitle: PropTypes.string,
    viewable: PropTypes.bool,
    handleDeleteRows: PropTypes.func.isRequired,
    handleCheckAllChange: PropTypes.func.isRequired,
    handleCheckChange: PropTypes.func.isRequired,
    handlePageNoChange: PropTypes.func.isRequired,
};

export default AnnotationsTableContainer;